// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { observer } from 'mobx-react';
import { observable, computed, makeObservable, action, runInAction } from 'mobx';
import cx from 'classnames';

import container from '@core/di';
import Asset, { AssetDTO } from '@shared/models/asset';
import { CustomerDTO, Customer } from '@shared/models/customers';
import { getQueriesAsSearch, getBase64FromUrl } from '@shared/utils/common';
import ROUTES from '@shared/constants/routes';
import { AssetType } from '@shared/constants/asset';
import { TOWER_FIELDS } from '@shared/constants/asset';
import AssetsStore from '@shared/stores/assets';
import CustomersStore from '@shared/stores/customers';
import EntitiesTemplatesStore, { ENTITIES } from '@core/stores/entities-templates';
import { resetForm } from '@shared/utils/form';
import { TowerForm } from './forms';
import Field from '@shared/components/Form/components/Field';
import Form, { ManageMode, FormProps, Data } from '@shared/components/Form';
import { showNotification, NotificationType } from '@shared/components/Notification';
import Tower from './components/Tower';
import { deleteFields } from '@shared/utils/form';
import { setFieldOption } from '@shared/components/Autocomplete';

import { ROLES } from '@shared/constants/user';
import AuthStore from '@core/stores/auth';

export type Params = {
  manageMode: string;
  id: string;
  type: string;
};

export interface AssetManagementProps {
  className?: string;
  manageMode: ManageMode;
  selectedCustomerId?: CustomerDTO['id'];
  id?: string;
  type?: AssetType;
  formComponentProps?: Partial<FormProps>;
  onSubmit?: (data: AssetDTO) => any;
}

@observer
class AssetManagement extends React.Component<AssetManagementProps> {
  private assetsStore = container.get<AssetsStore>(AssetsStore.diToken);
  private customersStore = container.get<CustomersStore>(CustomersStore.diToken);
  private entitiesTemplateStore = container.get<EntitiesTemplatesStore>(EntitiesTemplatesStore.diToken);
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  private towerForm;
  private canEditAsset: boolean;
  @observable asset: Asset | undefined = undefined;
  @observable customer: Customer | undefined = undefined;
  @observable currentType: AssetType = AssetType.tower;
  @observable storedData;
  @observable loading = false;

  constructor(props: AssetManagementProps) {
    super(props);

    this.towerForm = new TowerForm();
    this.storedData = undefined;

    this.manageBlockField();

    const allowedRoles = [ROLES.projectManager];
    this.canEditAsset = this.authStore.user.roles.some((role) => allowedRoles.includes(role));

    if (this.currentId || this.hasTemplate) {
      this.updateFormData();
    } else {
      this.towerForm.$(TOWER_FIELDS.customerId).set('value', props.selectedCustomerId);
      this.updateStoredData();
    }

    if (props.selectedCustomerId) {
      this.customersStore.get(props.selectedCustomerId).then((customer) => {
        this.customer = customer;
      });
    }
    makeObservable(this);
  }

  private manageBlockField = () => {
    const { asset, towerForm, props: { manageMode } } = this;

    if (manageMode === ManageMode.create) {
      const unnecessaryFieldsForCreate = [TOWER_FIELDS.isBlocked];

      deleteFields(towerForm, unnecessaryFieldsForCreate);
    }

    if (manageMode === ManageMode.edit && !towerForm.fields.has(TOWER_FIELDS.isBlocked)) {
      towerForm.add({
        key: TOWER_FIELDS.isBlocked,
        value: asset?.isBlocked
      });
    }
  }

  private get hasTemplate() {
    return this.entitiesTemplateStore.hasTemplate(ENTITIES.asset);
  }

  private get currentId() {
    return Number(this.props.id);
  }

  componentDidUpdate(prevProps: AssetManagementProps) {
    if (this.currentId && this.currentId !== Number(prevProps.id)) {
      this.manageBlockField();
      this.updateFormData();
    }
  }

  @action updateStoredData = (data: Data = this.currentForm.values()) => {
    this.storedData = data;
  }

  private processAssetData = async () => {
    // Logic should be rewritten after adding other asset types (this comment is from IntellectSoft)

    if (!this.asset) {
      return;
    }

    const formData = this.asset.asJson;

    try {
      formData.imageInfo = {
        image: await getBase64FromUrl(this.asset.assetImageUrl)
      };
    } catch {
      formData.imageInfo = {
        image: undefined
      };
    }

    const selectedCustomerId = this.selectedCustomer.id;

    if (this.withSelectedCustomer && selectedCustomerId) {
      formData.customerId = selectedCustomerId;
    }

    this.currentForm.update(formData);

    // Somehow, construction data cannot be updated (?!?)
    this.currentForm.$(TOWER_FIELDS.construction.name).$(TOWER_FIELDS.construction.fields.height).set('value', formData.construction?.height);
    this.currentForm.$(TOWER_FIELDS.construction.name).$(TOWER_FIELDS.construction.fields.type).set('value', formData.construction?.type);

    if (!this.withSelectedCustomer) {
      setFieldOption(
        this.currentForm.$(TOWER_FIELDS.customerId),
        { id: formData.customerId, name: formData.customerName || '' }
      );
    }

    this.updateStoredData();
    this.currentForm.validate();
  }

  private resetForm = () => {
    resetForm(this.currentForm);
    this.updateStoredData();
  }

  @action updateFormData = async () => {
    this.loading = true;

    const { manageMode } = this.props;
    const isCreating = manageMode === ManageMode.create;

    try {
      const templateId = this.entitiesTemplateStore.getTemplate(ENTITIES.asset);
      const assetId = isCreating ? templateId : this.currentId;

      const asset = await this.assetsStore.get(assetId, this.currentType);
      runInAction(() => {
        this.asset = asset;
      });

      await this.processAssetData();

      runInAction(() => {
        this.loading = false;
      });
    } catch {
      runInAction(() => {
        this.loading = false;
      });
      if (isCreating) {
        this.updateStoredData();

        return;
      }

      showNotification('Failed to load asset data', NotificationType.error);
    }
  }

  private get tabs() {
    const classes = withStyles.getClasses(this.props);
    const labels = [
      { type: AssetType.tower, name: 'Tower', disabled: false },
      { type: AssetType.mapping, name: 'Mapping', disabled: true, className: classes.middleTab },
      { type: AssetType.building, name: 'Building', disabled: true },
    ];

    return (
      <div className={classes.tabs}>
        {labels.map((tab) => (
          <div
            key={tab.type}
            data-id={tab.type}
            className={cx(
              classes.tab,
              tab.className,
              {
                [classes.tabActive]: this.currentType === tab.type,
                [classes.tabDisabled]: tab.disabled
              }
            )}
            onClick={this.handleTabChange}
          >
            {tab.disabled && <span className={classes.disabledLabel}>Comming soon</span>}
            {tab.name}
          </div>
        ))}
      </div>
    );
  }

  private get withSelectedCustomer() {
    const { manageMode, selectedCustomerId } = this.props;

    return selectedCustomerId || manageMode === ManageMode.edit; 
  }

  private get selectedCustomer() {
    const { selectedCustomerId } = this.props;

    return selectedCustomerId 
      ? { id: this.customer?.id, name: this.customer?.name }
      : { id: this.asset?.customerId, name: this.asset?.customerName }; 
  }

  private get tabContent() {
    const classes = withStyles.getClasses(this.props);
    const { towerForm } = this;
    const sectionClasses = {
      fieldLabel: classes.fieldLabel,
      fieldComponent: classes.fieldComponent,
      fieldRoot: classes.fieldRoot
    };
    const baseProps = { towerForm, classes: sectionClasses };

    const tabs = {
      [AssetType.tower]: (
        <Tower
          {...baseProps}
          selectedCustomerName={this.withSelectedCustomer ? this.selectedCustomer.name : undefined}
          form={this.currentForm}
        />
      ),
      [AssetType.mapping]: null,
      [AssetType.building]: null,
    };

    return tabs[this.currentType];
  }

  @computed private get currentForm() {
    const forms = {
      [AssetType.tower]: this.towerForm,
    };

    return forms[this.currentType];
  }

  private handleTabChange = (e) => {
    e.persist();

    this.currentType = Number(e.target.dataset.id);
  }

  private handleSetTemplateClick = () => {
    this.entitiesTemplateStore.setTemplate(ENTITIES.asset, this.currentId);
  }

  @action handleTowerSubmit = (data: AssetDTO, withCreateAnother: boolean) => {
    const { manageMode } = this.props;
    const imageFieldValue = this.currentForm.$(TOWER_FIELDS.imageInfo.name).$(TOWER_FIELDS.imageInfo.fields.image).value;
    const regex = /^data:image\/[a-z]+;base64,/;
    const imageFieldNormalizedValue = imageFieldValue ? imageFieldValue.replace(regex, '') : undefined;
    const isImageDeleted = !imageFieldValue && manageMode === ManageMode.edit;
    const imageInfo = imageFieldValue || isImageDeleted
      ? {
        image: imageFieldNormalizedValue,
        imageSet: isImageDeleted ? false : undefined
      }
      : undefined;

    const submitData: AssetDTO = {
      ...data,
      imageInfo
    };

    const methods = {
      [ManageMode.create]: async () => {
        try {
          const { assetId } = await this.assetsStore.create(submitData, undefined, this.currentType);
          const { onSubmit } = this.props;

          if (onSubmit) {
            onSubmit({ ...data, id: assetId, type: this.currentType });
          }

          if (withCreateAnother && this.hasTemplate && this.asset) {
            this.processAssetData();
          } else {
            this.resetForm();
          }
        } catch (err) {
          throw err;
        }
      },
      [ManageMode.edit]: async () => {
        try {
          await this.assetsStore.edit(this.currentId, submitData, undefined, this.currentType);

          this.asset?.update(data);
        } catch (err) {
          throw err;
        }
      },
    };

    return methods[manageMode]();
  }

  render() {
    const { manageMode, formComponentProps } = this.props;
    const classes = withStyles.getClasses(this.props);
    // console.log("**** HERE in BaseAssetManagement render");
    // console.log(this.currentForm);
    return (
      <Form
        withTemplate={this.canEditAsset}
        withCancelButtonOnCreate
        successMessage={manageMode === ManageMode.create
          ? 'Asset has been successfully created'
          : 'Asset has been successfully edited'
        }
        loadingData={this.loading}
        form={this.currentForm}
        manageMode={manageMode}
        heading={manageMode === ManageMode.create ? 'Add asset' : this.asset?.name}
        moduleName="Assets"
        classes={{
          root: classes.formRoot,
          controls: classes.controls
        }}
        storedData={this.storedData}
        updateStoredData={this.updateStoredData.bind(this)}
        onSubmit={this.handleTowerSubmit.bind(this)}
        onSetTemplateClick={this.handleSetTemplateClick.bind(this)}
        {...formComponentProps}
        closeLocation={formComponentProps?.closeLocation || {
          pathname: ROUTES.private.assets,
          search: getQueriesAsSearch()
        }}
        viewOnly={!this.canEditAsset}
      >
        <Field 
          label="Tower type" 
          component={this.tabs}
          classes={{ label: classes.fieldLabel, component: classes.fieldComponent, root: classes.fieldRoot }}
          />
        {this.tabContent}
      </Form>
    );
  }
}

const AssetManagementStyled = withStyles(
  AssetManagement, 
  (theme, props) => ({
    "formRoot": {
      width: 610
    },
    "tabsRoot": {},
    "controls": {
      paddingLeft: 'calc(30% + 24px)'
    },
    "tabs": {
      width: '100%',
      height: 48,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      border: `1px solid ${theme.palette.colors.primaryAccent}`,
      borderRadius: '4px'
    },
    "tab": {
      height: '100%',
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 1,
      padding: theme.spacing(2, 0),
      fontWeight: 500,
      color: theme.palette.colors.primaryAccent,
      cursor: 'pointer',
    },
    "middleTab": {
      borderLeft: `1px solid ${theme.palette.colors.primaryAccent}`,
      borderRight: `1px solid ${theme.palette.colors.primaryAccent}`,
    },
    "tabActive": {
      background: theme.palette.colors.primaryAccent,
      color: theme.palette.colors.white,
    },
    "tabDisabled": {
      color: 'rgba(209, 12,	12, 0.5)',
      pointerEvents: 'none',
    },
    "disabledLabel": {
      position: 'absolute',
      top: -10,
      fontSize: 10,
      fontWeight: 400,
      color: theme.palette.colors.primaryAccent,
      background: theme.palette.colors.white,
      border: `1px solid ${theme.palette.colors.primaryAccent}`,
      borderRadius: '20px',
      padding: theme.spacing(0, 2)
    },
    "tabContent": {
      width: '100%',
    },
    "fieldRoot": { },
    "fieldLabel": {
      flexWrap: 'wrap',
      width: '30%',
    },
    "fieldComponent": { }
  })
);

export default AssetManagementStyled;